import React, { ReactNode, useEffect, useMemo, useState } from 'react'

import { useFetcher } from 'react-router'

import { MODE } from '~/shared/components/DarkMode/constants.ts'
import { DarkModeContext } from '~/shared/components/DarkMode/context.ts'
import { setPreferColorSchemeCookie } from '~/shared/components/DarkMode/helpers.ts'
import { ACTION } from '~/shared/services/actions.ts'

interface DarkModeProviderProps {
  children: ReactNode,
  defaultMode?: MODE,
  preferredColorScheme?: MODE.LIGHT | MODE.DARK,
  disableColorSchemeListener?: boolean
}

export const DarkModeProvider = (
  {
    children,
    defaultMode = MODE.SYSTEM,
    preferredColorScheme,
    disableColorSchemeListener
  }: DarkModeProviderProps
) => {

  const [mode, setMode] = useState<MODE>(defaultMode)

  const [preferredColorSchemeValue, setPreferredColorScheme] = useState<MODE.LIGHT | MODE.DARK>(preferredColorScheme || MODE.LIGHT)

  const fetcher = useFetcher?.()

  const setGlobalMode = (newMode) => {
    setMode(newMode)
    let preferredColorScheme
    if (typeof globalThis.window !== 'undefined') {
      globalThis.window['ENV']['mode'] = newMode
      globalThis.window['setSystemTheme']?.()
      preferredColorScheme = globalThis.window.matchMedia('(prefers-color-scheme: dark)').matches ? MODE.DARK : MODE.LIGHT
    }
    fetcher?.submit?.({ mode: newMode, preferredColorScheme }, { method: 'POST', action: ACTION.SET_MODE(), })
  }

  const setLocalMode = (newMode) => {
    setMode(newMode)
    if (typeof globalThis.window !== 'undefined') {
      globalThis.window['ENV']['mode'] = newMode
      globalThis.window['setSystemTheme']?.()
    }
  }

  useEffect(() => {
    setMode(defaultMode)
  }, [defaultMode])

  useEffect(() => {
    if (disableColorSchemeListener) { return }
    // Preferred color scheme listener - update cookie if preferred color scheme changes
    if (typeof globalThis.window !== 'undefined') {
      const windowPreferredColorScheme = globalThis.window.matchMedia('(prefers-color-scheme: dark)').matches ? MODE.DARK : MODE.LIGHT

      if (preferredColorScheme !== windowPreferredColorScheme && !disableColorSchemeListener) {
        setPreferredColorScheme(windowPreferredColorScheme)
        setPreferColorSchemeCookie({ mode, preferredColorScheme: windowPreferredColorScheme, submitFunction: fetcher?.submit })
      }
    }
  }, [])

  const isDark = () => {
    if (typeof globalThis.window !== 'undefined') {
      return mode === MODE.DARK || (globalThis.window as any)?.ENV?.mode === MODE.DARK || ((globalThis.window as any)?.ENV?.mode === MODE.SYSTEM && globalThis.window?.matchMedia('(prefers-color-scheme: dark)')?.matches)
    }
    return mode === MODE.DARK || mode === MODE.SYSTEM && preferredColorSchemeValue === MODE.DARK
  }

  const isSystem = () => {
    if (typeof globalThis.window !== 'undefined') {
      return mode === MODE.SYSTEM || (globalThis.window as any)?.ENV?.mode === MODE.SYSTEM
    }
    return mode === MODE.SYSTEM
  }

  const contextValue = useMemo(
    () => ({ mode, setMode: setGlobalMode, isDark, setLocalMode, isSystem }),
    [mode, preferredColorSchemeValue]
  )

  return <DarkModeContext.Provider value={contextValue}><React.Fragment key={preferredColorSchemeValue}>{children}</React.Fragment></DarkModeContext.Provider>
}